import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  InjectionToken,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ConfigMessage, configMessage } from './config-message';
import { SitePreset } from '../data/site.model';
import { SitePresetPickerComponent } from '../ui/site-preset-picker.component';
import {
  IntegratorSelectComponent,
  IntegratorSelectOption,
  LibTooltipDirective,
  WhButtonComponent,
} from '@wheere-front/shared-ui';
import { IntegratorSiteFileMeta, IntegratorSiteService } from '@wheere/api-site';
import { SiteConfigurationService, SizeOption } from './site-configuration.service';
import { SiteFrequencyStore } from './site-frequency.store';
import {
  formatDelta,
  formatMHz,
  formatThetaDegrees,
  frequencyCoverage,
  frequencyFileStats,
  frequencyListWarnings,
  prettyFrequencyFile,
  reasonClause,
  warningBadge,
} from '../util/frequency-file';
import {
  CalibDialogComponent,
  FrequencyFileDialogComponent,
  FrequencyImportDialogComponent,
} from './frequency-dialogs.components';
import {
  AntennaTagComponent,
  downloadText,
  FrequencyModalComponent,
  ThetaSparkComponent,
  WarnBadgeComponent,
} from './frequency-ui.components';

/**
 * Lien vers Inside › Outils › Convertisseur (PLATFORM_ADMIN). Fourni par inside ; absent (null) dans
 * l'integrator ou sans le rôle : aucun bouton vers le Convertisseur.
 */
export const SITE_CONFIG_CONVERTER_LINK = new InjectionToken<string | null>('SITE_CONFIG_CONVERTER_LINK');

/**
 * Fichiers importés depuis la dernière validation (ADR-039), stockés au save. Vides en mode preset
 * (le serveur copie les fichiers du preset, `presetId` du layout) et côté integrator.
 */
export interface FilesState {
  frequencyFileName: string;
  /** Fichier de fréquences JSON tel que lu. */
  frequencyRawContent: string;
  globalCalibFileName: string;
  globalCalibRawContent: string;
}

export interface CoverageGatewayPosition {
  latitude?: number;
  longitude?: number;
}

/**
 * Section « Fichiers de calibration et taille » de l'onglet Configuration (ADR-039) : un fichier de
 * fréquences JSON (`freq`, `freq_delta`, `theta` 0-3600) et un calib, depuis un preset ou importés.
 * Inside : import contrôlé par le serveur (refus = fichier précédent gardé), résumé, antennes
 * A1..AN, avertissements jaunes non bloquants, motifs de blocage. Integrator (`presetOnly`) : choix
 * d'un preset, noms des fichiers seulement, jamais de contenu.
 */
@Component({
  selector: 'lib-files-section',
  standalone: true,
  imports: [
    WhButtonComponent,
    IntegratorSelectComponent,
    LibTooltipDirective,
    FormsModule,
    NgTemplateOutlet,
    RouterLink,
    SitePresetPickerComponent,
    TranslocoModule,
    WarnBadgeComponent,
    AntennaTagComponent,
    ThetaSparkComponent,
    FrequencyModalComponent,
    FrequencyFileDialogComponent,
    CalibDialogComponent,
    FrequencyImportDialogComponent,
  ],
  // L'état du fichier de fréquences vient de l'onglet Configuration (partagé avec les signal.json
  // générés) ; monté seul, la section a le sien.
  providers: [
    {
      provide: SiteFrequencyStore,
      useFactory: () => inject(SiteFrequencyStore, { skipSelf: true, optional: true }) ?? new SiteFrequencyStore(),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <ng-container *transloco="let t">
      <!-- Source : un preset de l'organisation ou un import personnalisé (inside). -->
      @if (!presetOnly()) {
        <div class="mb-3 flex items-center gap-3">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">{{
            t('siteConfiguration.files.source')
          }}</span>
          <div
            class="inline-flex rounded-full border border-gray-200 bg-gray-50 p-0.5 dark:border-slate-700 dark:bg-slate-900/60"
            role="radiogroup"
            [attr.aria-label]="t('siteConfiguration.files.sourceAriaLabel')">
            @for (m of sourceModes; track m.value) {
              <button
                type="button"
                role="radio"
                [attr.aria-checked]="sourceMode() === m.value"
                class="focus-visible:ring-primary-300 rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
                [class]="
                  sourceMode() === m.value
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100'
                "
                [disabled]="disabled()"
                (click)="setSourceMode(m.value)">
                {{ t(m.label) }}
              </button>
            }
          </div>
        </div>
      }

      @if (calibrationLocked()) {
        <div
          class="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
          role="note">
          <i class="pi pi-lock mt-0.5 text-xs"></i>
          <span>{{ t('siteConfiguration.customCalibrationLocked') }}</span>
        </div>
      } @else if (sourceMode() === 'preset') {
        <div class="mb-3">
          <lib-site-preset-picker
            [organizationId]="organizationId()"
            [integratorScope]="presetOnly()"
            [disabled]="disabled()"
            [(selectedPresetId)]="selectedPresetIdModel"
            (selected)="onPresetSelected($event)" />
        </div>
      }

      @if (presetOnly()) {
        <!-- Integrator : noms des fichiers du preset (ou du site verrouillé), jamais leur contenu. -->
        <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          @for (f of presetOnlyRows(); track f.key) {
            <div
              class="grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-x-3 border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-slate-700/60">
              <div class="flex items-center gap-2">
                <span
                  class="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  [class]="f.fileName ? 'bg-green-500' : 'bg-red-400'"></span>
                <span class="text-sm font-semibold text-gray-700 dark:text-slate-200">{{ t(f.label) }}</span>
              </div>
              @if (f.fileName) {
                <span
                  class="inline-flex max-w-full items-center gap-1 truncate rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-slate-700 dark:text-slate-200">
                  <i class="pi pi-file text-xs"></i>{{ f.fileName }}
                </span>
              } @else {
                <span class="text-xs italic text-gray-400 dark:text-slate-500">{{
                  t('siteConfiguration.files.pickPresetAbove')
                }}</span>
              }
            </div>
          }
        </div>
      } @else {
        @if (store.loadError(); as err) {
          <div
            class="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
            <i class="pi pi-times-circle mt-0.5 text-xs"></i>{{ t('siteConfiguration.frequency.loadError') }} {{ err }}
          </div>
        }

        <!-- Site non converti par la migration V41 : bloqué jusqu'à un nouveau fichier de fréquences. -->
        @if (store.migrationFailure(); as mf) {
          <div
            data-testid="migration-failure"
            class="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
            <i class="pi pi-times-circle mt-0.5 text-xs"></i>
            <div class="min-w-0 flex-1">
              <b>{{ t('siteConfiguration.frequency.noValidFile') }}</b>
              {{ t('siteConfiguration.frequency.migrationFailed', { reason: clause(mf.reason) }) }}
              @if (converterLink) {
                <div class="mt-1.5">
                  <a
                    [routerLink]="converterLink"
                    class="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:bg-slate-800 dark:text-red-300">
                    <i class="pi pi-sync text-[10px]"></i>{{ t('siteConfiguration.frequency.openConverter') }}
                  </a>
                </div>
              }
            </div>
          </div>
        }

        <!-- Fichier refusé à l'import : le fichier précédent reste en place. -->
        @if (store.rejection(); as rej) {
          <div
            data-testid="import-rejection"
            class="mb-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
            <i class="pi pi-times-circle mt-0.5 text-xs"></i>
            <div class="min-w-0 flex-1">
              <b>{{ t('siteConfiguration.frequency.rejected', { name: rej.fileName }) }}</b>
              — {{ keptText(rej.kind, t) }}
              <ul class="mt-1 list-disc space-y-0.5 pl-4">
                @for (l of rej.lines; track $index) {
                  <li>{{ l }}</li>
                }
              </ul>
              @if (rej.legacyFormat && converterLink) {
                <div class="mt-1.5">
                  <a
                    [routerLink]="converterLink"
                    class="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:bg-slate-800 dark:text-red-300">
                    <i class="pi pi-sync text-[10px]"></i>{{ t('siteConfiguration.frequency.convertInTools') }}
                  </a>
                </div>
              }
            </div>
          </div>
        }

        <!-- Fichiers : Fréquences + Calib. -->
        <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div
            class="grid grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
            <div>{{ t('siteConfiguration.files.columnFile') }}</div>
            <div>{{ t('siteConfiguration.files.columnDocument') }}</div>
            <div></div>
          </div>

          <!-- Fréquences -->
          <div
            data-testid="row-frequency"
            class="grid grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-gray-100 px-3 py-2 dark:border-slate-700/60">
            <div class="flex items-center gap-2">
              <span class="inline-block h-2.5 w-2.5 shrink-0 rounded-full" [class]="frequencyDot()"></span>
              <span class="text-sm font-semibold text-gray-700 dark:text-slate-200">{{
                t('siteConfiguration.frequency.rowFrequency')
              }}</span>
            </div>
            <div class="min-w-0">
              @if (file(); as f) {
                <span
                  class="inline-flex max-w-full cursor-pointer items-center gap-1 truncate rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400"
                  [libTooltip]="t('siteConfiguration.files.viewContent')"
                  libTooltipPosition="top"
                  (click)="dialog.set('frequency')">
                  <i class="pi pi-file text-xs"></i><span class="truncate">{{ f.fileName }}</span>
                </span>
                @if (importedLabel(); as d) {
                  <span class="text-pacificBlue-700 dark:text-pacificBlue-200 ml-1 text-[10px] font-semibold">{{
                    t('siteConfiguration.frequency.importedOn', { date: d })
                  }}</span>
                }
                @if (stats(); as s) {
                  <div
                    class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] tabular-nums text-gray-500 dark:text-slate-400">
                    <span
                      ><b class="text-gray-800 dark:text-slate-100">{{ s.count }}</b>
                      {{ t('siteConfiguration.frequency.frequenciesLower') }}</span
                    >
                    <span>{{ mhz2(s.minHz) }} → {{ mhz2(s.maxHz) }} MHz</span>
                    <span class="inline-flex flex-wrap items-center gap-1"
                      >freq_delta <b class="text-gray-800 dark:text-slate-100">{{ deltasUsed() }}</b>
                      {{ t('siteConfiguration.frequency.used', { count: deltasUsed() }) }}
                      @if (store.ignoredDeltas().length; as n) {
                        <lib-warn-badge>{{
                          t('siteConfiguration.frequency.badge.deltaIgnored', { count: n })
                        }}</lib-warn-badge>
                      }
                      @if (deltasMissing(); as n) {
                        ·
                        <b class="text-red-600 dark:text-red-400">{{
                          t('siteConfiguration.frequency.missing', { count: n })
                        }}</b>
                      }
                    </span>
                    <span
                      >theta {{ s.thetaMin }} → {{ s.thetaMax }}
                      <span class="text-gray-400 dark:text-slate-500"
                        >({{ deg(s.thetaMin) }} → {{ deg(s.thetaMax) }})</span
                      ></span
                    >
                    <lib-theta-spark [theta]="f.content.theta" />
                    @if (listWarnings().unsortedAt !== null) {
                      <lib-warn-badge>{{ t('siteConfiguration.frequency.badge.unsorted') }}</lib-warn-badge>
                    }
                    @if (listWarnings().duplicates; as n) {
                      <lib-warn-badge>{{
                        t('siteConfiguration.frequency.badge.duplicates', { count: n })
                      }}</lib-warn-badge>
                    }
                  </div>
                }
              } @else if (canEdit()) {
                <wh-button variant="outline" size="xs" (click)="importKind.set('frequency')">
                  <i class="pi pi-upload mr-1 text-xs"></i>{{ t('siteConfiguration.files.upload') }}
                </wh-button>
              } @else {
                <span class="text-xs italic text-gray-400 dark:text-slate-500">{{
                  t(
                    sourceMode() === 'preset'
                      ? 'siteConfiguration.files.pickPresetAbove'
                      : 'siteConfiguration.files.empty'
                  )
                }}</span>
              }
            </div>
            <div class="flex items-center justify-end gap-0.5">
              @if (file()) {
                <ng-container
                  *ngTemplateOutlet="
                    actions;
                    context: { kind: 'frequency', draft: store.hasDraftFile(), saved: hasSavedFile() }
                  " />
              }
            </div>
          </div>

          <!-- Calib -->
          <div data-testid="row-calib" class="grid grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2">
            <div class="flex items-center gap-2">
              <span class="inline-block h-2.5 w-2.5 shrink-0 rounded-full" [class]="calibDot()"></span>
              <span class="text-sm font-semibold text-gray-700 dark:text-slate-200">Calib</span>
            </div>
            <div class="min-w-0">
              @if (calib(); as c) {
                <span
                  class="inline-flex max-w-full cursor-pointer items-center gap-1 truncate rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400"
                  [libTooltip]="t('siteConfiguration.files.viewContent')"
                  libTooltipPosition="top"
                  (click)="dialog.set('calib')">
                  <i class="pi pi-file text-xs"></i><span class="truncate">{{ c.fileName }}</span>
                </span>
                <div
                  class="mt-1 flex flex-wrap items-center gap-1 text-[11px] tabular-nums text-gray-500 dark:text-slate-400">
                  {{ t('siteConfiguration.frequency.calibShape', { rows: c.rows }) }}
                  <b class="text-gray-800 dark:text-slate-100">{{ c.columns }}</b>
                  {{ t('siteConfiguration.frequency.columnsUsed', { used: calibUsed() }) }}
                  @if (calibIgnored(); as n) {
                    <lib-warn-badge>{{
                      t('siteConfiguration.frequency.badge.columnsIgnored', { count: n })
                    }}</lib-warn-badge>
                  }
                  @if (calibMissing(); as n) {
                    ·
                    <b class="text-red-600 dark:text-red-400">{{
                      t('siteConfiguration.frequency.columnsMissing', { count: n })
                    }}</b>
                  }
                  · {{ t('siteConfiguration.frequency.noDelta') }}
                </div>
              } @else if (canEdit()) {
                <wh-button variant="outline" size="xs" (click)="importKind.set('calib')">
                  <i class="pi pi-upload mr-1 text-xs"></i>{{ t('siteConfiguration.files.upload') }}
                </wh-button>
              } @else {
                <span class="text-xs italic text-gray-400 dark:text-slate-500">{{
                  t(
                    sourceMode() === 'preset'
                      ? 'siteConfiguration.files.pickPresetAbove'
                      : 'siteConfiguration.files.empty'
                  )
                }}</span>
              }
            </div>
            <div class="flex items-center justify-end gap-0.5">
              @if (calib()) {
                <ng-container
                  *ngTemplateOutlet="
                    actions;
                    context: { kind: 'calib', draft: store.hasDraftCalib(), saved: hasSavedCalib() }
                  " />
              }
            </div>
          </div>
        </div>

        @if (store.importing()) {
          <p class="mt-1.5 text-xs text-gray-500 dark:text-slate-400">
            <i class="pi pi-spin pi-spinner mr-1 text-xs"></i>{{ t('siteConfiguration.frequency.checking') }}
          </p>
        }

        <!-- Motifs de blocage (validation impossible tant qu'ils restent). -->
        @if (fileBlockers().length) {
          <div
            data-testid="frequency-blockers"
            class="mt-1.5 flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
            @for (b of fileBlockers(); track $index) {
              <div class="flex items-start gap-1.5">
                <i class="pi pi-exclamation-triangle mt-0.5 text-xs"></i
                ><span>{{ t(blockerKey(b.code), b.params) }}</span>
              </div>
            }
          </div>
        }

        <!-- Avertissements non bloquants. -->
        @if (warningLines(t).length) {
          <div
            class="mt-1.5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            <i class="pi pi-exclamation-triangle mt-0.5 text-xs"></i>
            <div class="min-w-0 flex-1">
              @for (w of warningLines(t); track $index) {
                <div>{{ w }}</div>
              }
              <span class="opacity-80">{{ t('siteConfiguration.frequency.nonBlocking') }}</span>
            </div>
          </div>
        }

        <!-- Antennes A1..AN : freq_delta appliqué, première → dernière fréquence émise, calib, gateway · DAC. -->
        @if (file(); as f) {
          <div class="mt-3" data-testid="antenna-table">
            <div class="mb-1.5 flex flex-wrap items-center gap-2 px-1">
              <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400"
                >{{ t('siteConfiguration.frequency.antennas') }} · {{ store.antennaCount() }}</span
              >
              <span class="text-[11px] text-gray-400 dark:text-slate-500">{{
                t('siteConfiguration.frequency.antennaRule')
              }}</span>
            </div>
            <div
              class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
              <div class="overflow-x-auto">
                <div class="min-w-[520px]">
                  <div
                    class="grid grid-cols-[3.25rem_5.5rem_minmax(0,1.6fr)_3.5rem_minmax(0,1fr)] items-center gap-x-2 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
                    <div>{{ t('siteConfiguration.frequency.colAntenna') }}</div>
                    <div class="text-right">freq_delta</div>
                    <div>{{ t('siteConfiguration.frequency.colEmits') }}</div>
                    <div class="text-right">Calib</div>
                    <div>{{ t('siteConfiguration.frequency.colGatewayDac') }}</div>
                  </div>
                  @for (a of store.antennas(); track a.index) {
                    <div
                      class="grid grid-cols-[3.25rem_5.5rem_minmax(0,1.6fr)_3.5rem_minmax(0,1fr)] items-center gap-x-2 border-b border-gray-100 px-3 py-1.5 text-xs last:border-b-0 dark:border-slate-700/60"
                      [class.bg-red-50]="a.freqDelta === null || a.dacConflict">
                      <div><lib-antenna-tag [index]="a.index" /></div>
                      <div
                        class="text-right font-semibold tabular-nums"
                        [class]="
                          a.freqDelta === null ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-slate-100'
                        ">
                        {{ a.freqDelta === null ? t('siteConfiguration.frequency.none') : delta(a.freqDelta) }}
                        <span class="block text-[10px] font-normal text-gray-400 dark:text-slate-500"
                          >freq_delta[{{ a.index }}]</span
                        >
                      </div>
                      <div class="min-w-0 truncate tabular-nums text-gray-700 dark:text-slate-200">
                        @if (a.firstHz !== null && a.lastHz !== null) {
                          {{ mhz(a.firstHz) }} → {{ mhz(a.lastHz) }} <span class="text-[10px] text-gray-400">MHz</span>
                        } @else {
                          <span class="text-red-600 dark:text-red-400">{{
                            t('siteConfiguration.frequency.noDeltaForAntenna')
                          }}</span>
                        }
                      </div>
                      <div
                        class="text-right tabular-nums"
                        [class]="
                          a.calibColumn
                            ? 'text-gray-700 dark:text-slate-200'
                            : 'font-semibold text-red-600 dark:text-red-400'
                        ">
                        {{
                          a.calibColumn
                            ? t('siteConfiguration.frequency.column', { n: a.calibColumn })
                            : t('siteConfiguration.frequency.none')
                        }}
                      </div>
                      <div
                        class="min-w-0 truncate text-[11px]"
                        [class]="
                          a.dacConflict
                            ? 'font-semibold text-red-600 dark:text-red-400'
                            : 'text-gray-500 dark:text-slate-400'
                        "
                        [title]="a.gatewayName + ' · ' + (a.dacPinned ? 'RF OUT ' + a.dac : 'RF OUT auto')">
                        GW{{ a.gatewayIndex }} · dac{{ a.dac }}
                      </div>
                    </div>
                  }
                  @for (d of store.ignoredDeltas(); track $index) {
                    <div
                      class="bg-warmYellow-50 grid grid-cols-[3.25rem_5.5rem_minmax(0,1.6fr)_3.5rem_minmax(0,1fr)] items-center gap-x-2 border-b border-gray-100 px-3 py-1 text-xs last:border-b-0 dark:border-slate-700/60 dark:bg-yellow-500/5">
                      <div><lib-antenna-tag [index]="store.antennaCount() + $index" [dim]="true" /></div>
                      <div class="text-right tabular-nums text-gray-500 dark:text-slate-400">{{ delta(d) }}</div>
                      <div class="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                        <lib-warn-badge>{{ t('siteConfiguration.frequency.ignoredOne') }}</lib-warn-badge>
                        {{
                          t('siteConfiguration.frequency.noAntenna', {
                            antenna: 'A' + (store.antennaCount() + $index + 1),
                          })
                        }}
                      </div>
                      <div></div>
                      <div></div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }

        <div
          class="mt-2 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
          <i class="pi pi-info-circle mt-0.5 text-xs"></i>{{ t('siteConfiguration.frequency.iqLogs') }}
        </div>
      }

      <!-- Taille -->
      <div class="mt-3 flex flex-wrap items-center gap-3 px-3">
        <div class="flex items-center gap-2">
          <span
            class="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            [class.bg-green-500]="selectedSize()"
            [class.bg-red-400]="!selectedSize()">
          </span>
          <span class="text-sm font-semibold text-gray-700 dark:text-slate-200">{{
            t('siteConfiguration.files.size')
          }}</span>
        </div>
        <lib-integrator-select
          width="8rem"
          size="sm"
          accent="neutral"
          [placeholder]="t('siteConfiguration.files.sizePlaceholder')"
          [options]="sizeOptions()"
          [ngModel]="selectedSize()"
          (ngModelChange)="onSizeChange($event)"
          [disabled]="disabled()" />
        @if (!presetOnly()) {
          <span class="text-[11px] text-gray-400 dark:text-slate-500">{{
            t('siteConfiguration.frequency.sizeHint')
          }}</span>
        }
      </div>

      @if (coverageStatus() !== 'none') {
        <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs">
          <span class="text-gray-500 dark:text-slate-400"
            >{{ t('siteConfiguration.files.freqCoverage') }} <strong>{{ freqCoverage() }}m</strong></span
          >
          <span class="text-gray-500 dark:text-slate-400"
            >{{ t('siteConfiguration.files.maxGatewayDistance') }} <strong>{{ maxGatewayDistance() }}m</strong></span
          >
          @if (coverageStatus() === 'warning') {
            <span class="font-medium text-red-600 dark:text-red-400">
              <i class="pi pi-exclamation-triangle mr-0.5 text-xs"></i
              >{{ t('siteConfiguration.files.freqInsufficient') }}
            </span>
          } @else {
            <span class="font-medium text-green-600 dark:text-green-400">
              <i class="pi pi-check-circle mr-0.5 text-xs"></i>OK
            </span>
          }
        </div>
      }

      <!-- Actions d'une ligne : aperçu, téléchargement, remplacer, supprimer / annuler l'import. -->
      <ng-template #actions let-kind="kind" let-draft="draft" let-saved="saved">
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
          [libTooltip]="t('siteConfiguration.files.viewContent')"
          [attr.aria-label]="t('siteConfiguration.files.viewContent')"
          libTooltipPosition="top"
          (click)="dialog.set(kind)">
          <i class="pi pi-eye text-sm"></i>
        </button>
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-md text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10"
          [libTooltip]="t('siteConfiguration.files.downloadFile')"
          [attr.aria-label]="t('siteConfiguration.files.downloadFile')"
          libTooltipPosition="top"
          (click)="download(kind)">
          <i class="pi pi-cloud-download text-sm"></i>
        </button>
        @if (canEdit()) {
          <button
            type="button"
            class="inline-flex size-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
            [libTooltip]="t('siteConfiguration.frequency.replace')"
            [attr.aria-label]="t('siteConfiguration.frequency.replace')"
            libTooltipPosition="top"
            (click)="importKind.set(kind)">
            <i class="pi pi-upload text-sm"></i>
          </button>
          @if (draft) {
            <button
              type="button"
              class="inline-flex size-8 items-center justify-center rounded-md text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
              [libTooltip]="t('siteConfiguration.frequency.cancelImport')"
              [attr.aria-label]="t('siteConfiguration.frequency.cancelImport')"
              libTooltipPosition="top"
              (click)="store.discardDraft(kind)">
              <i class="pi pi-times text-sm"></i>
            </button>
          } @else if (saved) {
            <button
              type="button"
              class="inline-flex size-8 items-center justify-center rounded-md text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
              [libTooltip]="t('siteConfiguration.files.removeFile')"
              [attr.aria-label]="t('siteConfiguration.files.removeFile')"
              libTooltipPosition="top"
              (click)="deleteCandidate.set(kind)">
              <i class="pi pi-trash text-sm"></i>
            </button>
          }
        }
      </ng-template>

      @if (dialog() === 'frequency' && file(); as f) {
        <lib-frequency-file-dialog
          [fileName]="f.fileName"
          [file]="f.content"
          [antennaCount]="store.antennaCount()"
          (closed)="dialog.set(null)" />
      }
      @if (dialog() === 'calib' && calib(); as c) {
        <lib-calib-dialog
          [fileName]="c.fileName"
          [raw]="c.raw"
          [rows]="c.rows"
          [columns]="c.columns"
          (closed)="dialog.set(null)" />
      }
      @if (importKind(); as k) {
        <lib-frequency-import-dialog
          [kind]="k"
          [context]="t('siteConfiguration.frequency.importContext', { count: store.antennaCount() })"
          (picked)="onPicked(k, $event)"
          (closed)="importKind.set(null)" />
      }
      @if (deleteCandidate(); as k) {
        <lib-frequency-modal
          [title]="t('siteConfiguration.frequency.deleteTitle')"
          widthClass="max-w-md"
          (closed)="deleteCandidate.set(null)">
          <p class="text-sm text-gray-600 dark:text-slate-300">
            {{
              t(
                k === 'frequency'
                  ? 'siteConfiguration.frequency.deleteFrequency'
                  : 'siteConfiguration.frequency.deleteCalib',
                {
                  name: k === 'frequency' ? file()?.fileName : calib()?.fileName,
                }
              )
            }}
          </p>
          @if (deleteError(); as e) {
            <p class="text-xs text-red-600 dark:text-red-400">{{ e }}</p>
          }
          <ng-container slot="footer">
            <wh-button variant="outline" size="sm" (click)="deleteCandidate.set(null)">{{
              t('siteConfiguration.cancel')
            }}</wh-button>
            <wh-button variant="destructive" size="sm" [disabled]="deleting()" (click)="confirmDelete(k)">{{
              t('siteConfiguration.frequency.deleteConfirm')
            }}</wh-button>
          </ng-container>
        </lib-frequency-modal>
      }
    </ng-container>
  `,
})
export class FilesSectionComponent implements OnInit {
  siteId = input<string | undefined>(undefined);
  disabled = input<boolean>(false);
  gatewayPositions = input<CoverageGatewayPosition[]>([]);
  /** Org id of the site — required for the preset picker to filter the catalog. */
  organizationId = input<string | null>(null);
  /** Preset actif du site (sites.active_preset_id) : la section s'ouvre en mode preset avec celui-ci. */
  activePresetId = input<string | null>(null);
  /** Fichiers uniquement par preset (integrator) : pas d'import, catalogue intégrateur sans contenu. */
  presetOnly = input<boolean>(false);
  /** Site à fichiers personnalisés Wheere (sans preset) : sélecteur masqué, fichiers conservés au save. */
  calibrationLocked = input<boolean>(false);
  /** Preset demandé depuis Site presets › « Appliquer à un site » (inside) : ouvert en mode preset,
   * à valider. Prime sur le preset actif du site. */
  requestedPresetId = input<string | null>(null);

  filesChanged = output<FilesState>();
  coverageChanged = output<number | null>();

  protected readonly store = inject(SiteFrequencyStore);
  private siteConfigService = inject(SiteConfigurationService);
  private integratorSites = inject(IntegratorSiteService);
  private cdr = inject(ChangeDetectorRef);
  private transloco = inject(TranslocoService);
  protected readonly converterLink = inject(SITE_CONFIG_CONVERTER_LINK, { optional: true }) ?? null;

  /** Couverture précalculée côté serveur du preset choisi (mode presetOnly). */
  private readonly presetCoverage = signal<number | null>(null);
  #siteFilesMetaLoaded = false;

  protected readonly sourceModes = [
    { value: 'preset' as const, label: 'siteConfiguration.files.fromPreset' },
    { value: 'custom' as const, label: 'siteConfiguration.files.customUpload' },
  ];

  sizes: SizeOption[] = this.siteConfigService.SIZES;
  private readonly sizeLabels = toSignal(
    this.transloco.selectTranslate<string[]>(this.sizes.map(s => s.labelKey).filter((k): k is string => !!k)),
    { initialValue: [] as string[] }
  );
  readonly sizeOptions = computed<IntegratorSelectOption<SizeOption>[]>(() => {
    const keyed = this.sizes.filter(s => s.labelKey);
    const labels = this.sizeLabels();
    return this.sizes.map(s => {
      const i = keyed.indexOf(s);
      return { label: i >= 0 && labels[i] ? labels[i] : s.label, value: s };
    });
  });

  /** Source : preset de l'organisation ou import personnalisé (état partagé avec le store). */
  readonly sourceMode = this.store.sourceMode;
  selectedPresetId = signal<string | null>(null);
  protected get selectedPresetIdModel(): string | null {
    return this.selectedPresetId();
  }
  protected set selectedPresetIdModel(v: string | null) {
    this.selectedPresetId.set(v);
  }

  selectedSize = signal<SizeOption | null>(null);
  protected readonly dialog = signal<'frequency' | 'calib' | null>(null);
  protected readonly importKind = signal<'frequency' | 'calib' | null>(null);
  protected readonly deleteCandidate = signal<'frequency' | 'calib' | null>(null);
  protected readonly deleting = signal(false);
  protected readonly deleteError = signal<string | null>(null);

  /** Integrator : noms des fichiers (preset choisi ou site verrouillé). */
  private readonly metaFrequencyName = signal('');
  private readonly metaCalibName = signal('');
  protected readonly presetOnlyRows = computed(() => [
    { key: 'frequency', label: 'siteConfiguration.frequency.rowFrequency', fileName: this.metaFrequencyName() },
    { key: 'calib', label: 'siteConfiguration.frequency.rowCalib', fileName: this.metaCalibName() },
  ]);

  protected readonly file = this.store.effectiveFile;
  protected readonly calib = this.store.effectiveCalib;
  protected readonly stats = computed(() => {
    const f = this.file();
    return f ? frequencyFileStats(f.content) : null;
  });
  protected readonly listWarnings = computed(() => frequencyListWarnings(this.file()?.content.freq ?? []));
  protected readonly deltasUsed = computed(() =>
    Math.min(this.store.antennaCount(), this.file()?.content.freq_delta.length ?? 0)
  );
  protected readonly deltasMissing = computed(() =>
    Math.max(0, this.store.antennaCount() - (this.file()?.content.freq_delta.length ?? 0))
  );
  protected readonly calibUsed = computed(() => Math.min(this.calib()?.columns ?? 0, this.store.antennaCount()));
  protected readonly calibIgnored = computed(() =>
    Math.max(0, (this.calib()?.columns ?? 0) - this.store.antennaCount())
  );
  protected readonly calibMissing = computed(() =>
    this.calib() ? Math.max(0, this.store.antennaCount() - this.calib()!.columns) : 0
  );
  protected readonly hasSavedFile = computed(() => !!this.store.setup()?.frequencyFile);
  protected readonly hasSavedCalib = computed(() => !!this.store.setup()?.calib);
  protected readonly canEdit = computed(() => !this.disabled() && this.sourceMode() === 'custom');

  /** Motifs affichés sous les fichiers (le DAC partagé l'est dans les gateways et les signal.json). */
  protected readonly fileBlockers = computed(() => this.store.blockers().filter(b => b.code !== 'DAC_SHARED'));

  protected readonly frequencyDot = computed(() => {
    const f = this.file();
    if (!f) return 'bg-red-400';
    return f.content.freq_delta.length >= this.store.antennaCount() ? 'bg-green-500' : 'bg-amber-400';
  });
  protected readonly calibDot = computed(() => {
    const c = this.calib();
    const f = this.file();
    const rowsOk = !f || !c || c.rows === f.content.freq.length;
    return c && rowsOk && c.columns >= this.store.antennaCount() ? 'bg-green-500' : 'bg-red-400';
  });

  /** « importé le … » : brouillon ou fichier déposé sur le site (rien pour un preset ou une migration). */
  protected readonly importedLabel = computed(() => {
    const f = this.file();
    if (!f || !f.importedAt) return null;
    if (f.source === 'preset' || (f.source === 'saved' && f.origin !== 'UPLOAD')) return null;
    const d = new Date(f.importedAt);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString(this.transloco.getActiveLang());
  });

  freqCoverage = computed<number | null>(() => {
    if (this.presetOnly()) return this.presetCoverage();
    const f = this.file();
    return f ? frequencyCoverage(f.content.freq) : null;
  });

  maxGatewayDistance = computed<number | null>(() => {
    const positions = this.gatewayPositions().filter(p => p.latitude != null && p.longitude != null);
    if (positions.length < 2) return null;
    let maxDist = 0;
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const d = this.haversineDistance(
          positions[i].latitude!,
          positions[i].longitude!,
          positions[j].latitude!,
          positions[j].longitude!
        );
        if (d > maxDist) maxDist = d;
      }
    }
    return Math.round(maxDist * 10) / 10;
  });

  coverageStatus = computed<'ok' | 'warning' | 'none'>(() => {
    const coverage = this.freqCoverage();
    const distance = this.maxGatewayDistance();
    if (coverage == null || distance == null) return 'none';
    return distance > coverage ? 'warning' : 'ok';
  });

  protected readonly clause = reasonClause;
  protected readonly blockerKey = (code: string) => `siteConfiguration.frequency.blockers.${code}`;
  protected readonly mhz = (hz: number) => formatMHz(hz);
  protected readonly mhz2 = (hz: number) => formatMHz(hz, 2);
  protected readonly deg = formatThetaDegrees;
  protected readonly delta = formatDelta;

  /** Supprime le passage « dirty » au préchargement du preset actif du site. */
  private suppressPresetDirty = false;
  private presetSeeded = false;

  constructor() {
    effect(() => {
      const id = this.requestedPresetId();
      if (!id || this.presetOnly() || this.presetSeeded) return;
      this.presetSeeded = true;
      this.sourceMode.set('preset');
      this.selectedPresetId.set(id);
      untracked(() => this.siteConfigService.markFilesChanged());
    });

    effect(() => {
      const id = this.activePresetId();
      if (!id || this.presetSeeded) return;
      this.presetSeeded = true;
      this.sourceMode.set('preset');
      this.suppressPresetDirty = true;
      this.selectedPresetId.set(id);
    });

    // Verrou intégrateur : métadonnées des fichiers du site à la place du preset.
    effect(() => {
      const siteId = this.siteId();
      if (!this.presetOnly() || !this.calibrationLocked() || !siteId || this.#siteFilesMetaLoaded) return;
      this.#siteFilesMetaLoaded = true;
      this.loadSiteFileMeta(siteId);
    });

    effect(() => this.coverageChanged.emit(this.freqCoverage()));
  }

  ngOnInit(): void {
    if (this.presetOnly()) {
      this.sourceMode.set('preset');
      return;
    }
    this.store.active.set(true);
    const siteId = this.siteId();
    if (siteId) void this.store.load(siteId);
  }

  protected setSourceMode(mode: 'preset' | 'custom'): void {
    if (this.sourceMode() === mode) return;
    this.sourceMode.set(mode);
    this.siteConfigService.markFilesChanged();
    this.emitFilesState();
  }

  protected keptText(kind: 'frequency' | 'calib', t: (k: string, p?: Record<string, unknown>) => string): string {
    if (kind === 'calib') {
      const c = this.calib();
      return c
        ? t('siteConfiguration.frequency.keptCalib', { name: c.fileName })
        : t('siteConfiguration.frequency.noCalibInPlace');
    }
    const f = this.file();
    return f
      ? t('siteConfiguration.frequency.keptFile', { name: f.fileName, count: f.content.freq.length })
      : t('siteConfiguration.frequency.noFileInPlace');
  }

  /** Avertissements non bloquants détaillés (liste non triée, doublons, autres avertissements serveur). */
  protected warningLines(t: (k: string, p?: Record<string, unknown>) => string): string[] {
    const f = this.file()?.content;
    const out: string[] = [];
    if (f) {
      const w = this.listWarnings();
      if (w.unsortedAt !== null) {
        const k = w.unsortedAt;
        out.push(
          t('siteConfiguration.frequency.unsortedDetail', {
            k,
            prev: k - 1,
            value: formatMHz(f.freq[k]),
            prevValue: formatMHz(f.freq[k - 1]),
          })
        );
      }
      if (w.duplicates) out.push(t('siteConfiguration.frequency.duplicatesDetail', { count: w.duplicates }));
    }
    const local = new Set(['FREQ_UNSORTED', 'FREQ_DUPLICATES', 'FREQ_DELTA_IGNORED', 'CALIB_COLUMNS_IGNORED']);
    for (const w of this.store.importWarnings()) {
      if (!local.has(w.code)) out.push(w.message || t(warningBadge(w).key, warningBadge(w).params));
    }
    return out;
  }

  protected async onPicked(kind: 'frequency' | 'calib', file: File): Promise<void> {
    this.importKind.set(null);
    const siteId = this.siteId();
    if (!siteId) return;
    if (kind === 'frequency') await this.store.importFrequencyFile(siteId, file);
    else await this.store.importCalib(siteId, file);
    this.emitFilesState();
    this.cdr.markForCheck();
  }

  protected download(kind: 'frequency' | 'calib'): void {
    if (kind === 'frequency') {
      const f = this.file();
      if (f) downloadText(f.fileName, prettyFrequencyFile(f.content));
      return;
    }
    const c = this.calib();
    if (c?.raw) downloadText(c.fileName, c.raw, 'text/plain');
  }

  protected async confirmDelete(kind: 'frequency' | 'calib'): Promise<void> {
    const siteId = this.siteId();
    if (!siteId) return;
    this.deleting.set(true);
    this.deleteError.set(null);
    const err = await this.store.deleteSaved(siteId, kind);
    this.deleting.set(false);
    if (err) {
      this.deleteError.set(err);
      return;
    }
    this.deleteCandidate.set(null);
    this.emitFilesState();
  }

  /** Métadonnées des fichiers du site (`GET /integrator/sites/{id}/files`, sans `rawContent`). */
  private loadSiteFileMeta(siteId: string): void {
    this.integratorSites.getIntegratorSiteFiles(siteId).subscribe({
      next: (files: IntegratorSiteFileMeta[]) => {
        const byType = (t: string) => files?.find(f => f.fileType === t);
        this.applyFileNames(byType('frequency_file')?.fileName, byType('calib_file')?.fileName);
      },
    });
  }

  /** Mode presetOnly : noms et états sans contenu (rien n'est déposé au save). */
  private applyFileNames(frequencyName: string | undefined, calibName: string | undefined): void {
    this.metaFrequencyName.set(frequencyName ?? '');
    this.metaCalibName.set(calibName ?? '');
    this.siteConfigService.setFrequenciesLoaded(!!frequencyName);
    this.siteConfigService.setGlobalCalibLoaded(!!calibName);
    this.emitFilesState();
    this.cdr.detectChanges();
  }

  onSizeChange(size: SizeOption): void {
    this.selectedSize.set(size);
    this.siteConfigService.selectedSize.set(size);
    this.siteConfigService.markDirty();
  }

  setSizeFromStore(size: SizeOption): void {
    this.selectedSize.set(size);
  }

  /** Preset choisi dans le sélecteur (`null` : sélection vidée, rien ne change). */
  onPresetSelected(preset: SitePreset | null): void {
    if (this.presetOnly()) {
      this.onPresetOnlySelected(preset);
      return;
    }
    this.store.preset.set(preset);
    if (!preset) return;
    if (this.suppressPresetDirty) this.suppressPresetDirty = false;
    else this.siteConfigService.markFilesChanged();
    this.emitFilesState();
  }

  /** Preset seul : le serveur matérialise les fichiers du preset au `PATCH layout` (`presetId`). */
  private onPresetOnlySelected(preset: SitePreset | null): void {
    if (this.calibrationLocked()) return;
    if (!preset) {
      this.presetCoverage.set(null);
      this.applyFileNames(undefined, undefined);
      return;
    }
    this.presetCoverage.set(preset.freqCoverage ?? null);
    this.applyFileNames(preset.frequencyFileName ?? 'frequences.json', preset.calibFileName ?? 'calib.txt');
    if (this.suppressPresetDirty) this.suppressPresetDirty = false;
    else this.siteConfigService.markDirty();
  }

  /** Preset actif à persister au save : l'id choisi, en mode « Depuis un preset » seulement. */
  getActivePresetId(): string | null {
    if (this.calibrationLocked()) return null;
    return this.sourceMode() === 'preset' ? this.selectedPresetId() : null;
  }

  /** Brouillons à stocker au save (import personnalisé d'inside). */
  getFilesState(): FilesState {
    const { frequency, calib } = this.presetOnly() ? { frequency: null, calib: null } : this.store.draftFiles();
    return {
      frequencyFileName: frequency?.fileName ?? '',
      frequencyRawContent: frequency?.raw ?? '',
      globalCalibFileName: calib?.fileName ?? '',
      globalCalibRawContent: calib?.raw ?? '',
    };
  }

  /** Contrôle avant la confirmation de validation : premier motif bloquant (inside). */
  validateFileLines(): { isValid: boolean; message: ConfigMessage | null } {
    if (this.presetOnly()) return { isValid: true, message: null };
    const b = this.store.blockers()[0];
    return b
      ? { isValid: false, message: configMessage(this.blockerKey(b.code), b.params) }
      : { isValid: true, message: null };
  }

  hasValidationError(): boolean {
    return false;
  }

  /** Après une validation réussie : relit l'état du site (brouillons stockés). */
  afterSave(): void {
    const siteId = this.siteId();
    if (!this.presetOnly() && siteId) void this.store.afterSave(siteId);
  }

  private emitFilesState(): void {
    this.filesChanged.emit(this.getFilesState());
  }

  private haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}
